import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getStats, 
  getQuotes, 
  updateQuoteStatus, 
  deleteQuote,
  getGalleryImages,
  addGalleryImage,
  deleteGalleryImage,
  getServicesContent,
  updateServicesContent,
  getCommercialShowcase,
  updateCommercialShowcase,
  resetUserPassword,
  getAllUsers,
  createUser,
  deleteUser,
  logout,
  getCurrentUser
} from '../services/adminapi';
import { 
  LogOut, 
  Mail, 
  Phone, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Trash2,
  Plus,
  X,
  BarChart3,
  Image as ImageIcon,
  MessageSquare,
  Layout,
  Save,
  Settings,
  Key,
  Users
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user] = useState(getCurrentUser());
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [services, setServices] = useState([]);
  const [commercialShowcase, setCommercialShowcase] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [showAddImage, setShowAddImage] = useState(false);
  const [newImage, setNewImage] = useState({ url: '', title: '', category: 'concrete-cutting' });
  
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newPasswordForUser, setNewPasswordForUser] = useState('');
  
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', full_name: '' });

  const loadStats = useCallback(async () => {
    try {
      const data = await getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const filter = statusFilter === 'all' ? null : statusFilter;
      const data = await getQuotes(filter);
      setQuotes(data);
    } catch (error) {
      console.error('Failed to load quotes:', error);
    }
    setLoading(false);
  }, [statusFilter]);

  const loadGallery = useCallback(async () => {
    try {
      const data = await getGalleryImages();
      setGallery(data);
    } catch (error) {
      console.error('Failed to load gallery:', error);
    }
  }, []);

  const loadSiteContent = useCallback(async () => {
    try {
      const servicesData = await getServicesContent();
      setServices(Array.isArray(servicesData.content) ? servicesData.content : []);
      const showcaseData = await getCommercialShowcase();
      setCommercialShowcase(Array.isArray(showcaseData.content) ? showcaseData.content : []);
    } catch (error) {
      console.error('Failed to load site content:', error);
      setServices([]);
      setCommercialShowcase([]);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadQuotes();
    loadGallery();
    loadSiteContent();
    if (user?.role === 'super_admin') {
      loadUsers();
    }
  }, [loadStats, loadQuotes, loadGallery, loadSiteContent, loadUsers, user?.role]);

  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  const handleSaveServices = async () => {
    try {
      await updateServicesContent(services);
      alert('Services updated successfully!');
      loadStats();
    } catch (error) {
      alert('Failed to update services: ' + error.message);
    }
  };

  const handleSaveShowcase = async () => {
    try {
      await updateCommercialShowcase(commercialShowcase);
      alert('Commercial showcase updated successfully!');
      loadStats();
    } catch (error) {
      alert('Failed to update showcase: ' + error.message);
    }
  };

  const updateServiceImage = (serviceId, newImageUrl) => {
    setServices(services.map(s => 
      s.id === serviceId ? { ...s, image: newImageUrl } : s
    ));
  };

  const updateShowcaseImage = (index, newImageUrl) => {
    const updated = [...commercialShowcase];
    updated[index] = { ...updated[index], image: newImageUrl };
    setCommercialShowcase(updated);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!selectedUserForPassword) return;
    try {
      await resetUserPassword(selectedUserForPassword.id, newPasswordForUser);
      alert('Password reset successfully for ' + selectedUserForPassword.email);
      setShowPasswordChange(false);
      setSelectedUserForPassword(null);
      setNewPasswordForUser('');
    } catch (error) {
      alert('Failed to reset password: ' + error.message);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await createUser(newUser.email, newUser.password, newUser.full_name);
      alert('User created successfully!');
      setShowAddUser(false);
      setNewUser({ email: '', password: '', full_name: '' });
      loadUsers();
    } catch (error) {
      alert('Failed to create user: ' + error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await deleteUser(userId);
      loadUsers();
    } catch (error) {
      alert('Failed to delete user: ' + error.message);
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await updateQuoteStatus(quoteId, newStatus);
      loadQuotes();
      loadStats();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Delete this quote?')) return;
    try {
      await deleteQuote(quoteId);
      loadQuotes();
      loadStats();
    } catch (error) {
      alert('Failed to delete quote');
    }
  };

  const handleAddImage = async (e) => {
    e.preventDefault();
    try {
      await addGalleryImage(newImage.url, newImage.title, newImage.category);
      setNewImage({ url: '', title: '', category: 'concrete-cutting' });
      setShowAddImage(false);
      loadGallery();
      loadStats();
    } catch (error) {
      alert('Failed to add image');
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await deleteGalleryImage(imageId);
      loadGallery();
      loadStats();
    } catch (error) {
      alert('Failed to delete image');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
            <p className="text-gray-400 text-sm">South Palmyra Inc.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-white font-medium">{user?.full_name}</p>
              <p className="text-gray-400 text-sm">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-4 overflow-x-auto">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'quotes', label: 'Quotes', icon: MessageSquare },
              { id: 'gallery', label: 'Gallery', icon: ImageIcon },
              { id: 'photos', label: 'Site Photos', icon: Layout },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={activeTab === tab.id ? 'flex items-center gap-2 px-6 py-4 font-medium border-b-2 text-orange-500 border-orange-500 whitespace-nowrap' : 'flex items-center gap-2 px-6 py-4 font-medium border-b-2 text-gray-400 border-transparent hover:text-white whitespace-nowrap'}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">

        {activeTab === 'dashboard' && stats && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Statistics</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard title="Total Quotes" value={stats.total_quotes} icon={MessageSquare} color="blue" />
              <StatCard title="Today" value={stats.quotes_today} icon={Clock} color="green" />
              <StatCard title="This Week" value={stats.quotes_this_week} icon={CheckCircle} color="orange" />
              <StatCard title="Gallery Images" value={stats.gallery_images} icon={ImageIcon} color="purple" />
            </div>
            <div className="bg-slate-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">Quotes by Status</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-slate-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Pending</span>
                    <AlertCircle className="w-5 h-5 text-yellow-500" />
                  </div>
                  <p className="text-3xl font-bold text-white mt-2">{stats.quotes_by_status.pending}</p>
                </div>
                <div className="bg-slate-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Contacted</span>
                    <Phone className="w-5 h-5 text-blue-500" />
                  </div>
                  <p className="text-3xl font-bold text-white mt-2">{stats.quotes_by_status.contacted}</p>
                </div>
                <div className="bg-slate-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Completed</span>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                  <p className="text-3xl font-bold text-white mt-2">{stats.quotes_by_status.completed}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'quotes' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Quotes Management</h2>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="contacted">Contacted</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            {loading ? (
              <p className="text-gray-400">Loading...</p>
            ) : quotes.length === 0 ? (
              <div className="bg-slate-800 rounded-lg p-12 text-center">
                <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No quotes found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {quotes.map(quote => (
                  <QuoteCard key={quote.id} quote={quote} onStatusChange={handleStatusChange} onDelete={handleDeleteQuote} />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'gallery' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Gallery Management</h2>
              <button onClick={() => setShowAddImage(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors">
                <Plus className="w-5 h-5" />
                Add Image
              </button>
            </div>
            {showAddImage && (
              <div className="bg-slate-800 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white">Add New Image</h3>
                  <button onClick={() => setShowAddImage(false)} className="text-gray-400 hover:text-white">
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <form onSubmit={handleAddImage} className="space-y-4">
                  <input type="url" required placeholder="Image URL" value={newImage.url} onChange={(e) => setNewImage({...newImage, url: e.target.value})} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                  <input type="text" required placeholder="Title" value={newImage.title} onChange={(e) => setNewImage({...newImage, title: e.target.value})} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                  <select value={newImage.category} onChange={(e) => setNewImage({...newImage, category: e.target.value})} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                    <option value="concrete-cutting">Concrete Cutting</option>
                    <option value="windows">Windows</option>
                    <option value="demolition">Demolition</option>
                    <option value="drainage">Drainage</option>
                  </select>
                  <button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg transition-colors">Add Image</button>
                </form>
              </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {gallery.map(image => (
                <div key={image.id} className="bg-slate-800 rounded-lg overflow-hidden group">
                  <div className="relative">
                    <img src={image.url} alt={image.title} className="w-full h-48 object-cover" />
                    <button onClick={() => handleDeleteImage(image.id)} className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-white">{image.title}</h3>
                    <p className="text-sm text-gray-400">{image.category}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'photos' && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Site Photos Management</h2>
            <div className="bg-slate-800 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Services Images</h3>
                <button onClick={handleSaveServices} className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors">
                  <Save className="w-5 h-5" />Save Services
                </button>
              </div>
              {Array.isArray(services) && services.length > 0 ? (
                <div className="grid md:grid-cols-3 gap-6">
                  {services.map(service => (
                    <div key={service.id} className="bg-slate-700 rounded-lg p-4">
                      <img src={service.image} alt={service.title} className="w-full h-48 object-cover rounded-lg mb-4" />
                      <h4 className="font-semibold text-white mb-2">{service.title}</h4>
                      <input type="url" value={service.image} onChange={(e) => updateServiceImage(service.id, e.target.value)} className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white text-sm" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400">Loading services...</p>
              )}
            </div>
            <div className="bg-slate-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Commercial Showcase Images</h3>
                <button onClick={handleSaveShowcase} className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors">
                  <Save className="w-5 h-5" />Save Showcase
                </button>
              </div>
              {Array.isArray(commercialShowcase) && commercialShowcase.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {commercialShowcase.map((item, index) => (
                    <div key={index} className="bg-slate-700 rounded-lg p-4">
                      <img src={item.image} alt={'Showcase ' + (index + 1)} className="w-full h-48 object-cover rounded-lg mb-4" />
                      <input type="url" value={item.image} onChange={(e) => updateShowcaseImage(index, e.target.value)} className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white text-sm" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400">Loading showcase...</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Settings</h2>
            {user?.role === 'super_admin' ? (
              <div className="bg-slate-800 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-6 h-6 text-orange-500" />
                    <h3 className="text-xl font-bold text-white">User Management</h3>
                  </div>
                  <button onClick={() => setShowAddUser(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors">
                    <Plus className="w-5 h-5" />Add Admin
                  </button>
                </div>
                {showAddUser && (
                  <form onSubmit={handleCreateUser} className="bg-slate-700 rounded-lg p-4 mb-4 space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-white">Create New Admin</h4>
                      <button type="button" onClick={() => setShowAddUser(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
                    </div>
                    <input type="text" required placeholder="Full Name" value={newUser.full_name} onChange={(e) => setNewUser({...newUser, full_name: e.target.value})} className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded text-white" />
                    <input type="email" required placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded text-white" />
                    <input type="password" required minLength="8" placeholder="Password (min 8 characters)" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded text-white" />
                    <button type="submit" className="w-full bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg transition-colors">Create Admin User</button>
                  </form>
                )}
                {showPasswordChange && selectedUserForPassword && (
                  <form onSubmit={handleChangePassword} className="bg-slate-700 rounded-lg p-4 mb-4 space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-white">Reset Password for {selectedUserForPassword.email}</h4>
                      <button type="button" onClick={() => { setShowPasswordChange(false); setSelectedUserForPassword(null); setNewPasswordForUser(''); }} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
                    </div>
                    <input type="password" required minLength="8" placeholder="New Password (min 8 characters)" value={newPasswordForUser} onChange={(e) => setNewPasswordForUser(e.target.value)} className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded text-white" />
                    <button type="submit" className="w-full bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg transition-colors">Reset Password</button>
                  </form>
                )}
                <div className="space-y-3">
                  {users.map(u => (
                    <div key={u.id} className="bg-slate-700 rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-white">{u.full_name}</h4>
                        <p className="text-sm text-gray-400">{u.email}</p>
                        <div className="flex gap-2 mt-1">
                          <span className={u.role === 'super_admin' ? 'text-xs px-2 py-1 rounded bg-orange-500/20 text-orange-500' : 'text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-500'}>
                            {u.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                          </span>
                          {u.email === user.email && (
                            <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-500">You</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => { setSelectedUserForPassword(u); setShowPasswordChange(true); }} className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
                          <Key className="w-4 h-4" />
                        </button>
                        {u.email !== 'admin@southpalmyra.com' && u.id !== user.id && (
                          <button onClick={() => handleDeleteUser(u.id)} className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-slate-800 rounded-lg p-12 text-center">
                <Settings className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">You don't have access to settings. Contact super admin.</p>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => {
  const colors = { blue: 'bg-blue-500', green: 'bg-green-500', orange: 'bg-orange-500', purple: 'bg-purple-500' };
  return (
    <div className="bg-slate-800 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="text-gray-400">{title}</span>
        <div className={'p-3 ' + colors[color] + ' rounded-lg'}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
    </div>
  );
};

const QuoteCard = ({ quote, onStatusChange, onDelete }) => {
  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-500',
    contacted: 'bg-blue-500/20 text-blue-500',
    completed: 'bg-green-500/20 text-green-500'
  };
  return (
    <div className="bg-slate-800 rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-white">{quote.name}</h3>
          <p className="text-gray-400">{quote.service}</p>
        </div>
        <span className={'px-3 py-1 rounded-full text-sm font-medium ' + statusColors[quote.status]}>
          {quote.status}
        </span>
      </div>
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-gray-300">
          <Mail className="w-4 h-4" />
          <span>{quote.email}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-300">
          <Phone className="w-4 h-4" />
          <span>{quote.phone}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <Clock className="w-4 h-4" />
          {new Date(quote.timestamp).toLocaleString()}
        </div>
      </div>
      <div className="bg-slate-700 rounded-lg p-4 mb-4">
        <p className="text-gray-300 whitespace-pre-wrap">{quote.message}</p>
      </div>
      {quote.photo_url && (
        <div className="mb-4">
          <img src={quote.photo_url} alt="Quote" className="rounded-lg max-h-48 object-cover" />
        </div>
      )}
      <div className="flex items-center gap-2">
        <select value={quote.status} onChange={(e) => onStatusChange(quote.id, e.target.value)} className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
          <option value="pending">Pending</option>
          <option value="contacted">Contacted</option>
          <option value="completed">Completed</option>
        </select>
        <button onClick={() => onDelete(quote.id)} className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors">
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default AdminDashboard;
